import Lightbox from "yet-another-react-lightbox";
import type { RenderSlideProps } from "yet-another-react-lightbox";

import { DecodedSlide } from "./DecodedSlide.tsx";
import { SPOTLIGHT_COPY } from "./spotlight.constants.ts";
import type { SpotlightItem } from "./spotlight.model.ts";
import type { SpotlightView } from "./useSpotlight.ts";
import "yet-another-react-lightbox/styles.css";
import "./spotlight.css";

/**
 * Two slide types of our own, declared to the library.
 *
 * Its own image slide handles a JPEG. A PDF and a document that cannot be shown
 * are ours, and this augmentation is the only place the library's types meet
 * this app's — which is why it lives in the `.tsx` and not in the model.
 */
declare module "yet-another-react-lightbox" {
  interface SlideTypes {
    inline: { type: "inline"; item: Extract<SpotlightItem, { kind: "inline" }> };
    decodable: {
      type: "decodable";
      item: Extract<SpotlightItem, { kind: "decodable" }>;
    };
    unavailable: {
      type: "unavailable";
      item: Extract<SpotlightItem, { kind: "unavailable" }>;
    };
  }
}

/**
 * The viewer: one shared overlay, wherever something is worth opening.
 *
 * It renders nothing at all when shut, so the library is out of the tree for
 * every screen that is not currently showing a document.
 *
 * The mapping below is a switch over a union the model already decided, which
 * is the one kind of branching a view is allowed: there is no rule here about
 * which files can be displayed — that is `toSpotlightItem`, and it is tested.
 */
export function Spotlight({ view, onClose }: { view: SpotlightView; onClose: () => void }) {
  if (view.kind === "shut") return null;

  return (
    <Lightbox
      open
      close={onClose}
      index={view.kind === "open" ? view.index : 0}
      slides={view.kind === "open" ? view.items.map(toSlide) : []}
      controller={{ closeOnBackdropClick: true }}
      labels={{ Close: SPOTLIGHT_COPY.close }}
      render={{ slide: renderSlide }}
      className="spotlight"
      // The library's own tokens, pointed at ONDO's. Every value it exposes is
      // a custom property, which is why this stylesheet needs no overrides.
      styles={{ container: { backgroundColor: "var(--spotlight-backdrop)" } }}
    />
  );
}

function toSlide(item: SpotlightItem) {
  if (item.kind === "image") {
    return { type: "image" as const, src: item.url, alt: item.caption, description: item.caption };
  }
  if (item.kind === "inline") return { type: "inline" as const, item };
  if (item.kind === "decodable") return { type: "decodable" as const, item };

  return { type: "unavailable" as const, item };
}

function renderSlide({ slide }: RenderSlideProps) {
  if (slide.type === "inline") {
    return (
      <figure className="spotlight__frame">
        <iframe className="spotlight__pdf" src={slide.item.url} title={slide.item.caption} />
        <figcaption className="spotlight__caption">
          {slide.item.caption}
          {/* Some mobile browsers refuse to inline a PDF and leave the frame
              blank. The way out is always on the page rather than only in a
              menu. */}
          <a href={slide.item.url} target="_blank" rel="noreferrer">
            {SPOTLIGHT_COPY.openInTab}
          </a>
        </figcaption>
      </figure>
    );
  }

  // A hook lives in there, so it is a component and not a branch of this
  // function. See `DecodedSlide`.
  if (slide.type === "decodable") return <DecodedSlide item={slide.item} />;

  if (slide.type === "unavailable") {
    return (
      <figure className="spotlight__frame">
        <div className="spotlight__unavailable">
          <p className="spotlight__reason">{slide.item.reason}</p>
          {slide.item.url !== null && (
            <a
              className="spotlight__download"
              href={slide.item.url}
              target="_blank"
              rel="noreferrer"
              download
            >
              {SPOTLIGHT_COPY.download}
            </a>
          )}
        </div>
        <figcaption className="spotlight__caption">{slide.item.caption}</figcaption>
      </figure>
    );
  }

  // Undefined hands the slide back to the library, which is what renders an
  // image. Returning our own element for it would reimplement its zoom and
  // sizing for no gain.
  return undefined;
}
